import {
    AreaChart, Area, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine
} from 'recharts';
import { ArrowUpRight, Minus, CheckCircle, Activity, HelpCircle } from 'lucide-react';


const CompetencyTrajectoryChart = ({ title, data, status, average, currentScore, scaleMax = 6, threshold }) => {
    // data expected: [{ session_id, date, score }] (score can be null)

    // Filter out null scores for the chart to avoid breaks or handle them
    // Recharts handles nulls by breaking the line, which is usually fine.
    // But let's format the date for XAxis
    const chartData = data.map(d => ({
        ...d,
        displayDate: new Date(d.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
        score: d.score // keep as is
    }));

    const getStatusBadge = (status) => {
        switch (status) {
            case 'Emerging':
                return (
                    <div className="flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-semibold border border-blue-200" title="Skill showing consistent upward development">
                        <ArrowUpRight size={14} />
                        <span>Emerging</span>
                    </div>
                );
            case 'Stabilized':
                return (
                    <div className="flex items-center gap-1 px-2 py-1 bg-emerald-50 text-emerald-700 rounded-full text-xs font-semibold border border-emerald-200" title="Skill consistently demonstrated at competent level">
                        <CheckCircle size={14} />
                        <span>Stabilized</span>
                    </div>
                );
            case 'Inconsistent':
                return (
                    <div className="flex items-center gap-1 px-2 py-1 bg-amber-50 text-amber-700 rounded-full text-xs font-semibold border border-amber-200" title="Skill demonstrated variably across sessions">
                        <Activity size={14} />
                        <span>Inconsistent</span>
                    </div>
                );
            case 'Start':
                return (
                    <div className="flex items-center gap-1 px-2 py-1 bg-gray-50 text-gray-600 rounded-full text-xs font-medium" title="Not enough data points">
                        <Minus size={14} />
                        <span>Start</span>
                    </div>
                );
            default: // Developing / Unknown
                return (
                    <div className="flex items-center gap-1 px-2 py-1 bg-gray-50 text-gray-600 rounded-full text-xs font-medium" title="Skill still in development">
                        <HelpCircle size={14} />
                        <span>Developing</span>
                    </div>
                );
        }
    };

    // "Premium 2D" Theme
    const getTheme = (status, currentScore, scaleMax, threshold) => {
        const failureThreshold = threshold !== undefined ? threshold : (scaleMax * 0.5);
        if (currentScore != null && currentScore < failureThreshold) {
            return {
                stroke: '#EF4444', // Red 500
                fillStart: '#EF4444',
                fillEnd: '#FEE2E2', // Red 100
                bg: 'bg-red-50/30'
            };
        }
        switch (status) {
            case 'Stabilized': return {
                stroke: '#10B981', // Emerald 500
                fillStart: '#10B981',
                fillEnd: '#D1FAE5', // Emerald 100
                bg: 'bg-emerald-50/30'
            };
            case 'Emerging': return {
                stroke: '#3B82F6', // Blue 500
                fillStart: '#3B82F6',
                fillEnd: '#DBEAFE', // Blue 100
                bg: 'bg-blue-50/30'
            };
            case 'Inconsistent': return {
                stroke: '#F59E0B', // Amber 500
                fillStart: '#F59E0B',
                fillEnd: '#FEF3C7', // Amber 100
                bg: 'bg-amber-50/30'
            };
            default: return {
                stroke: '#8B5CF6', // Violet 500
                fillStart: '#8B5CF6',
                fillEnd: '#EDE9FE', // Violet 100
                bg: 'bg-violet-50/30'
            };
        }
    };

    const theme = getTheme(status, currentScore, scaleMax, threshold);
    const gradientId = `gradient-${title.replace(/\s+/g, '-')}`;

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className="bg-white/90 border border-gray-100 p-3 rounded-xl shadow-xl backdrop-blur-md">
                    <p className="text-xs text-gray-400 font-medium mb-1">{label}</p>
                    <div className="flex items-baseline gap-1">
                        <span className="text-2xl font-bold text-gray-800 tracking-tight">{payload[0].value?.toFixed(1)}</span>
                        <span className="text-xs text-gray-500 font-medium">/ {scaleMax.toFixed(1)}</span>
                    </div>
                </div>
            );
        }
        return null;
    };

    return (
        <div className={`relative h-64 w-full bg-white rounded-3xl p-6 shadow-sm border border-gray-100 transition-all duration-300 hover:shadow-md group overflow-hidden`}>
            {/* Contextual Background - Subtle tint */}
            <div className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 ${theme.bg}`}></div>

            <div className="relative z-10 flex flex-col h-full">
                {/* Header */}
                <div className="flex justify-between items-start mb-6">
                    <div>
                        <h4 className="font-bold text-gray-900 text-lg tracking-tight mb-1 capitalize">{title}</h4>
                        <div className="flex items-center gap-2">
                            <div className="flex items-baseline gap-1">
                                <span className="text-2xl font-black text-gray-900">{currentScore ? currentScore.toFixed(1) : '-'}</span>
                                <span className="text-sm font-semibold text-gray-400 uppercase tracking-wide">Current</span>
                            </div>
                            {average && (
                                <span className="text-xs font-semibold text-gray-400 bg-gray-50 px-2 py-0.5 rounded-md">
                                    AVG {average.toFixed(1)}
                                </span>
                            )}
                        </div>
                    </div>
                    {getStatusBadge(status)}
                </div>

                {/* Chart Area */}
                <div className="w-full" style={{ height: 110 }}>
                    <ResponsiveContainer width="100%" height={110}>
                        <AreaChart data={chartData} margin={{ top: 5, right: 0, left: -20, bottom: 0 }}>
                            <defs>
                                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor={theme.fillStart} stopOpacity={0.3} />
                                    <stop offset="95%" stopColor={theme.fillEnd} stopOpacity={0.0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                            <XAxis
                                dataKey="displayDate"
                                tick={{ fontSize: 10, fill: '#9CA3AF', fontWeight: 500 }}
                                axisLine={false}
                                tickLine={false}
                                dy={10}
                                minTickGap={30}
                            />
                            <YAxis
                                domain={[0, scaleMax]}
                                hide={true}
                            />
                            <Tooltip content={<CustomTooltip />} cursor={{ stroke: theme.stroke, strokeWidth: 2, strokeDasharray: '4 4' }} />
                            <Area
                                type="monotone"
                                dataKey="score"
                                stroke={theme.stroke}
                                strokeWidth={3}
                                fillOpacity={1}
                                fill={`url(#${gradientId})`}
                                animationDuration={1500}
                                animationEasing="ease-out"
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
};

export default CompetencyTrajectoryChart;
